import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { FontProvider } from './contexts/FontContext';
import { CartProvider } from './contexts/CartContext';
import { ShopAuthProvider } from './contexts/ShopAuthContext';
import './utils/autoScale'; // Initialize smart auto-scale system
import './utils/heroAdjustment'; // Initialize hero section adjustment
import './utils/modalScrollFix'; // Initialize modal scroll fixes
import './utils/smartViewportDetection'; // Initialize smart viewport detection
import './styles/imageOptimization.css'; // Image optimization styles
import { logDomainInfo } from './utils/domainDetection'; // Domain detection utility
import Header from './components/Header/Header';
import Hero from './components/Hero/Hero';
import Loader from './components/Loader/Loader';
import FloatingNav from './components/FloatingNav/FloatingNav';
import SEO from './components/SEO/SEO';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminLayout from './pages/Admin/AdminLayout';
import AdminHome from './pages/Admin/AdminHome';
import Menubar from './pages/Admin/Menubar/Menubar';
import Settings from './pages/Admin/Settings/Settings';
import Authors from './pages/Admin/Settings/Authors';
import SocialMedia from './pages/Admin/Settings/SocialMedia';
import HeaderSection from './pages/Admin/HeaderSection/HeaderSection';
import AboutMe from './pages/Admin/AboutMe/AboutMe';
import FontManagement from './pages/Admin/FontManagement/FontManagement';
import ProjectManagement from './pages/Admin/ProjectManagement/ProjectManagement';
import CategoryManagement from './pages/Admin/CategoryManagement/CategoryManagement';
import SubcategoryManagement from './pages/Admin/CategoryManagement/SubcategoryManagement';
import BlogPreview from './pages/Admin/BlogPreview';
import BlogManagement from './pages/Admin/BlogManagement/BlogManagement';
import BlogRichTextEditor from './pages/Admin/BlogManagement/BlogRichTextEditor';
import BlogCategoryManagement from './pages/Admin/BlogCategoryManagement/BlogCategoryManagement';
import ContactSettings from './pages/Admin/ContactManagement/ContactSettings';
import ContactResponses from './pages/Admin/ContactManagement/ContactResponses';
import MapUpload from './pages/Admin/SpatialCanvas/MapUpload';
import MapCategoryManagement from './pages/Admin/SpatialCanvas/MapCategoryManagement';
import FeaturedMapsManagement from './pages/Admin/SpatialCanvas/FeaturedMapsManagement';
import CertificateUpload from './pages/Admin/Certificate/CertificateUpload';
import CertificateCategoryManagement from './pages/Admin/Certificate/CertificateCategoryManagement';
import FeaturedCertificatesManagement from './pages/Admin/Certificate/FeaturedCertificatesManagement';
import ExperienceManagement from './pages/Admin/Experience/ExperienceManagement';
import GalleryManagement from './pages/Admin/ProjectGallery/GalleryManagement';
import PresentationManagement from './pages/Admin/Presentation/PresentationManagement';
import ProductManagement from './pages/Admin/Shop/ProductManagement';
import ShopCategoryManagement from './pages/Admin/Shop/CategoryManagement';
import CouponManagement from './pages/Admin/Shop/CouponManagement';
import OrderManagement from './pages/Admin/Shop/OrderManagement';
import NewsletterManagement from './pages/Admin/Newsletter/NewsletterManagement';
import ShortlinkManagement from './pages/Admin/ShortlinkManagement/ShortlinkManagement';
import AnalyticsDashboard from './pages/Admin/Analytics/AnalyticsDashboard';
import AllContentAnalytics from './pages/Admin/Analytics/AllContentAnalytics';
import ContentAnalytics from './pages/Admin/Analytics/ContentAnalytics';
import RichTextEditorPage from './pages/Admin/RichTextEditorPage';
import ProjectEditorPage from './pages/Admin/ProjectEditorPage';
import RichTextEditorPreview from './pages/Admin/RichTextEditorPreview';
import Login from './pages/Admin/Login/Login';
import FloatingCart from './components/FloatingCart/FloatingCart';
import ShortlinkRedirect from './components/ShortlinkRedirect/ShortlinkRedirect';
import Footer from './components/Footer/Footer';
import { databaseService } from './lib/appwrite';
import { loadAllCustomFonts } from './utils/fontLoader';
import AnalyticsWrapper from './components/AnalyticsWrapper/AnalyticsWrapper';
import './App.css';

// Lazy load other sections for better performance
const About = lazy(() => import('./components/About/About'));
const SkilledOn = lazy(() => import('./components/SkilledOn/SkilledOn'));
const EducationalQualification = lazy(() => import('./components/EducationalQualification/EducationalQualification'));
const Projects = lazy(() => import('./components/Projects/Projects'));
const Research = lazy(() => import('./components/Research/Research'));
const Contact = lazy(() => import('./components/Contact/Contact'));
const SpatialCanvas = lazy(() => import('./components/SpatialCanvas/SpatialCanvas'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const SpatialCanvasPage = lazy(() => import('./pages/SpatialCanvasPage'));
const ProjectPage = lazy(() => import('./pages/ProjectPage'));

// Blog components
const Blog = lazy(() => import('./components/Blog/Blog'));
const BlogsPage = lazy(() => import('./pages/BlogsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));

// Map page
const MapPage = lazy(() => import('./pages/MapPage'));

// Certificate components
const Certificate = lazy(() => import('./components/Certificate/Certificate'));
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'));
const CertificatePage = lazy(() => import('./pages/CertificatePage'));

// Experience component
const Experience = lazy(() => import('./components/Experience/Experience'));

// Project Gallery components
const ProjectGallery = lazy(() => import('./components/ProjectGallery/ProjectGallery'));
const ProjectGalleryPage = lazy(() => import('./pages/ProjectGalleryPage'));
const PresentationPage = lazy(() => import('./pages/PresentationPage'));

// Contact page
const ContactPage = lazy(() => import('./pages/ContactPage'));

// About page
const AboutPage = lazy(() => import('./pages/AboutPage'));

// Prefetching disabled to prevent navigation issues
// const prefetchPages = () => {
//   ...
// };

// Shop components
const Shop = lazy(() => import('./components/Shop/Shop'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));

// Dashboard components
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const OrderStatus = lazy(() => import('./pages/Dashboard/OrderStatus'));
const Support = lazy(() => import('./pages/Dashboard/Support'));



function Portfolio() {
  // Enable loader for homepage
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navType = useNavigationType();
  
  useEffect(() => {
    // Show loader for 3 seconds (one full animation cycle)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  // Handle scroll to section from navigation state
  useEffect(() => {
    // IGNORE scroll reset on POP (back/forward) to let browser restore position
    if (navType === 'POP') return;

    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      
        // Create a function to handle safe scrolling that accounts for layout shifts
        const handleScroll = (behavior = 'smooth') => {
          const el = document.getElementById(sectionId);
          if (el) {
            const rect = el.getBoundingClientRect();
            const scrollTop = window.scrollY + rect.top - 70; // 70px offset matching FloatingNav
            window.scrollTo({ top: scrollTop, behavior });
          }
        };

        // Attempt scroll immediately and then retry to account for lazy loading
        setTimeout(() => handleScroll('auto'), 100);
        setTimeout(() => handleScroll('smooth'), 500); // Adjust for loaded content
        setTimeout(() => handleScroll('smooth'), 1200); // Final adjustment
        
        // Clear state to prevent re-scrolling on refresh/update
        window.history.replaceState({}, document.title);
    } else {
      // Global scroll reset on route change (if no specific scroll target)
      // explicit 'instant' behavior overrides CSS 'smooth'
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  }, [location.pathname, location.state, navType]);

  // Save and restore scroll position on refresh
  useEffect(() => {
    // Save scroll position before page unload
    const saveScrollPosition = () => {
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    };

    window.addEventListener('beforeunload', saveScrollPosition);

    // Restore scroll position after content loads
    const restoreScrollPosition = () => {
      const savedPosition = sessionStorage.getItem('scrollPosition');
      if (savedPosition && !isLoading) {
        // Wait a bit for content to render
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedPosition, 10),
            behavior: 'instant'
          });
          // Clear the saved position after restoring
          sessionStorage.removeItem('scrollPosition');
        }, 100);
      }
    };

    if (!isLoading) {
      // Restore scroll position
      restoreScrollPosition();
    }

    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [isLoading]);

  return (
    <AnalyticsWrapper 
      contentType="page" 
      contentId="homepage" 
      contentSlug="home"
      enableScrollTracking={true}
    >
      <div 
        className={`App relative overflow-hidden ${isLoading ? 'app-loading' : 'app-loaded'}`}
        style={{ 
          background: '#FFFAEB',
          backgroundImage: `linear-gradient(rgba(16, 86, 82, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 86, 82, 0.05) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          backgroundPosition: '0 0',
          minHeight: '100vh'
        }}
      >

      {isLoading && <Loader />}
      <Header />
      {!isLoading && <FloatingNav />}
      <SEO 
        title="Parvej Hossain - Portfolio"
        description="Parvej Hossain - GIS Enthusiast, Cartographer, Data Analyst, and Environmental Researcher based in Bangladesh."
        keywords="Parvej Hossain, GIS, Cartography, Data Analyst, Environmental Researcher, Portfolio, Bangladesh"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Parvej Hossain",
          "url": "https://bd.parvej.me",
          "jobTitle": "GIS Analyst & Researcher",
          "sameAs": [
            "https://www.linkedin.com/in/parvej-hossain",
            "https://github.com/parvej-hossain" 
          ]
        }}
      />
      <main className={`relative z-10 ${isLoading ? 'content-hidden' : 'content-visible'}`}>
        <Hero />
        
        {/* Sections now load in parallel for better performance */}
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <SkilledOn />
        </Suspense>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <EducationalQualification />
        </Suspense>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <About />
        </Suspense>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Projects />
        </Suspense>

        <div id="spatial-canvas" className="scroll-mt-20">
          <Suspense fallback={<div className="min-h-[400px]" />}>
            <SpatialCanvas />
          </Suspense>
        </div>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Certificate />
        </Suspense>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Experience />
        </Suspense>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <ProjectGallery />
        </Suspense>

        <div id="blog" className="scroll-mt-20">
          <Suspense fallback={<div className="min-h-[400px]" />}>
            <Blog />
          </Suspense>
        </div>

        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Shop />
        </Suspense>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <Contact />
        </Suspense>

        <Footer />
      </main>
    </div>
    </AnalyticsWrapper>
  );
}

function App() {
  // Log domain information on app load for debugging
  useEffect(() => {
    logDomainInfo();
  }, []);

  // Load site settings (title, favicon, and custom fonts) for ALL routes
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const SETTINGS_COLLECTION = 'site_settings';
        const result = await databaseService.listDocuments(SETTINGS_COLLECTION);
        if (result.success && result.data.documents.length > 0) {
          const settings = result.data.documents[0];
          
          // Update site title
          if (settings.siteTitle) {
            document.title = settings.siteTitle;
          } else {
            document.title = 'Portfolio of PARVEJ';
          }
          
          // Update favicon
          if (settings.faviconUrl) {
            updateFavicon(settings.faviconUrl);
          }
        } else {
          document.title = 'Portfolio of PARVEJ';
        }
      } catch (error) {
        console.error('Error loading site settings:', error);
        document.title = 'Portfolio of PARVEJ';
      }
    };

    // Load custom fonts
    const loadCustomFonts = async () => {
      try {
        const result = await databaseService.listDocuments('custom_fonts');
        if (result.success && result.data.documents.length > 0) {
          loadAllCustomFonts(result.data.documents);
        }
      } catch (error) {
        console.error('Error loading custom fonts:', error);
      }
    };

    const updateFavicon = (url) => {
      if (!url || typeof url !== 'string') {
        console.error('Invalid favicon URL:', url);
        return;
      }

      // Remove all existing favicon links
      const existingLinks = document.querySelectorAll("link[rel*='icon'], link[rel='shortcut icon']");
      existingLinks.forEach(link => link.remove());

      // Detect file type from URL
      const urlLower = url.toLowerCase();
      let type = 'image/x-icon';
      if (urlLower.includes('.png')) {
        type = 'image/png';
      } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
        type = 'image/jpeg';
      } else if (urlLower.includes('.svg')) {
        type = 'image/svg+xml';
      } else if (urlLower.includes('.webp')) {
        type = 'image/webp';
      } else if (urlLower.includes('.ico')) {
        type = 'image/x-icon';
      }

      // Create new favicon links (multiple for browser compatibility)
      const link1 = document.createElement('link');
      link1.rel = 'icon';
      link1.type = type;
      link1.href = url;
      document.head.appendChild(link1);

      const link2 = document.createElement('link');
      link2.rel = 'shortcut icon';
      link2.type = type;
      link2.href = url;
      document.head.appendChild(link2);

      // Force browser to reload favicon by adding timestamp
      const link3 = document.createElement('link');
      link3.rel = 'icon';
      link3.type = type;
      link3.href = `${url}?t=${Date.now()}`;
      document.head.appendChild(link3);
    };

    loadSiteSettings();
    loadCustomFonts();
  }, []);

  // Production-safe auto-scale initialization
  useEffect(() => {
    const ensureAutoScale = () => {
      // Check if auto-scale is working
      const autoScaleValue = getComputedStyle(document.documentElement).getPropertyValue('--auto-scale');
      
      if (!autoScaleValue || autoScaleValue.trim() === '') {
        // Auto-scale not detected, force initialization
        console.log('🔧 Auto-scale not detected in production, forcing initialization...');
        
        // Import and reinitialize auto-scale
        import('./utils/autoScale').then(module => {
          const autoScale = module.default;
          if (autoScale && autoScale.recalculate) {
            autoScale.recalculate();
          }
        });
        
        // Add class to indicate auto-scale is ready
        setTimeout(() => {
          document.documentElement.classList.add('auto-scale-ready');
        }, 500);
      } else {
        document.documentElement.classList.add('auto-scale-ready');
      }
    };

    // Run after a short delay to ensure DOM is ready
    const timer = setTimeout(ensureAutoScale, 1000);
    
    // Also run on window load as backup
    window.addEventListener('load', ensureAutoScale);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', ensureAutoScale);
    };
  }, []);

  // Use native browser scroll restoration for best performance and exact positioning
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <FontProvider>
          <CartProvider>
            <ShopAuthProvider>
            <BrowserRouter>
            <Routes>
            <Route path="/admin/login" element={<Login />} />
            {/* Rich Text Editor - Standalone page without layout */}
            <Route 
              path="/admin/rich-text-editor" 
              element={
                <ProtectedRoute>
                  <RichTextEditorPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Rich Text Editor - Blog Mode */}
            <Route 
              path="/admin/rich-text-editor/blog/:blogId" 
              element={
                <ProtectedRoute>
                  <RichTextEditorPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Rich Text Editor - Project Mode */}
            <Route 
              path="/admin/rich-text-editor/project/:projectId" 
              element={
                <ProtectedRoute>
                  <ProjectEditorPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Legacy routes - redirect to new format */}
            <Route 
              path="/admin/rich-text-editor/:blogId" 
              element={
                <ProtectedRoute>
                  <RichTextEditorPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/admin/project-editor/:projectId" 
              element={
                <ProtectedRoute>
                  <ProjectEditorPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Blog Preview - Standalone page */}
            <Route 
              path="/admin/blog-preview" 
              element={
                <ProtectedRoute>
                  <BlogPreview />
                </ProtectedRoute>
              } 
            />
            
            {/* Blog Rich Text Editor - Standalone page */}
            <Route 
              path="/admin/blog-rich-text-editor" 
              element={
                <ProtectedRoute>
                  <BlogRichTextEditor />
                </ProtectedRoute>
              } 
            />
            {/* Rich Text Editor Preview - With site header */}
            <Route 
              path="/admin/rich-text-editor/preview" 
              element={
                <ProtectedRoute>
                  <RichTextEditorPreview />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AnalyticsDashboard />} />
              <Route path="menubar" element={<Menubar />} />
              <Route path="header-section" element={<HeaderSection />} />
              <Route path="about-me" element={<AboutMe />} />
              <Route path="projects" element={<ProjectManagement />} />
              <Route path="categories" element={<CategoryManagement />} />
              <Route path="subcategories" element={<SubcategoryManagement />} />
              <Route path="blogs" element={<BlogManagement />} />
              <Route path="blog-categories" element={<BlogCategoryManagement />} />
              <Route path="contact-settings" element={<ContactSettings />} />
              <Route path="contact-responses" element={<ContactResponses />} />
              <Route path="map-upload" element={<MapUpload />} />
              <Route path="map-categories" element={<MapCategoryManagement />} />
              <Route path="featured-maps" element={<FeaturedMapsManagement />} />
              <Route path="certificate-upload" element={<CertificateUpload />} />
              <Route path="certificate-categories" element={<CertificateCategoryManagement />} />
              <Route path="featured-certificates" element={<FeaturedCertificatesManagement />} />
              <Route path="experiences" element={<ExperienceManagement />} />
              <Route path="project-gallery" element={<GalleryManagement />} />
              <Route path="presentations" element={<PresentationManagement />} />
              <Route path="products" element={<ProductManagement />} />
              <Route path="shop-categories" element={<ShopCategoryManagement />} />
              <Route path="coupons" element={<CouponManagement />} />
              <Route path="orders" element={<OrderManagement />} />
              <Route path="newsletter" element={<NewsletterManagement />} />
              <Route path="shortlinks" element={<ShortlinkManagement />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="analytics/all" element={<AllContentAnalytics />} />
              <Route path="analytics/:contentType/:contentId" element={<ContentAnalytics />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/authors" element={<Authors />} />
              <Route path="settings/social-media" element={<SocialMedia />} />
              <Route path="font-management" element={<FontManagement />} />
            </Route>
            <Route path="/projects" element={
              <Suspense fallback={<div className="min-h-screen" />}>
                <ProjectsPage />
              </Suspense>
            } />
            {/* Blog Page Routes */}
            <Route path="/blogs" element={
              <Suspense fallback={<div className="min-h-screen" />}>
                <BlogsPage />
              </Suspense>
            } />
            {/* Spatial Canvas Page Route */}
            <Route path="/spatial-canvas" element={
              <Suspense fallback={<div className="min-h-screen" />}>
                <SpatialCanvasPage />
              </Suspense>
            } />
            {/* Single Map Page Route */}
            <Route path="/map/:slug" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <MapPage />
              </Suspense>
            } />
            {/* Certificate Routes */}
            <Route path="/certificates" element={
              <Suspense fallback={<div className="min-h-screen" />}>
                <CertificatesPage />
              </Suspense>
            } />
            <Route path="/certificate/:slug" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#dc2626' }}></div></div>}>
                <CertificatePage />
              </Suspense>
            } />
            {/* Project Gallery Route */}
            <Route path="/project-gallery" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#ec4899' }}></div></div>}>
                <ProjectGalleryPage />
              </Suspense>
            } />
            {/* Presentation Route */}
            <Route path="/presentation" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <PresentationPage />
              </Suspense>
            } />
            {/* Contact Page Route */}
            <Route path="/contact" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <ContactPage />
              </Suspense>
            } />
            {/* About Page Route */}
            <Route path="/about" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <AboutPage />
              </Suspense>
            } />
            {/* Shop Route */}
            <Route path="/shop" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <ShopPage />
              </Suspense>
            } />
            {/* Product Page Routes - with and without prefix */}
            <Route path="/product/:slug" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <ProductPage />
              </Suspense>
            } />
            {/* Cart Route */}
            <Route path="/cart" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <CartPage />
              </Suspense>
            } />
            {/* Checkout Route */}
            <Route path="/checkout" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <CheckoutPage />
              </Suspense>
            } />
            {/* Dashboard Routes */}
            <Route path="/dashboard" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <Dashboard />
              </Suspense>
            } />
            <Route path="/dashboard/order/:orderId" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <OrderStatus />
              </Suspense>
            } />
            <Route path="/dashboard/support" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <Support />
              </Suspense>
            } />
            <Route path="/blog/:slug" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <BlogPage />
              </Suspense>
            } />
            {/* Project Page Routes - with and without prefix */}
            <Route path="/project/:slug" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div></div>}>
                <ProjectPage />
              </Suspense>
            } />

            {/* Shortlink redirect handler - must come before catch-all */}
            {/* Handles both simple paths and paths with prefixes like group1/link */}
            <Route path="/:path" element={<ShortlinkRedirect />} />
            <Route path="/:prefix/:path" element={<ShortlinkRedirect />} />
            <Route path="/*" element={<Portfolio />} />
            </Routes>
            <FloatingCart />
            </BrowserRouter>
            </ShopAuthProvider>
          </CartProvider>
        </FontProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
